import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DataRoomMember } from '../../models/DataRoomMember';
import { SortingButton } from '../SortingButton';
import { Loader } from 'ncoded-component-library';
import dataRoomApi from '../../dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { BODY_CONTENT_GENERATE } from './components/CellComponents';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import {
  SORTING_VALUES,
  TABLE_COLUMNS,
  COPY_HEADING_CELL,
  POPUP_SORT_NAME,
} from './components/CellComponents/data';
import { FIRST_PAGE } from 'data/global';

import './BuyAcessTable.styles.scss';
import './BuyAcessTable.styles.responsive.scss';

type BuyAcessTableProps = {
  className?: string;
};

const BuyAcessTable: React.FC<BuyAcessTableProps> = (props) => {
  const { className } = props;

  const [members, setMembers] = useState<DataRoomMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortApplied, setSortApplied] = useState(`-${SORTING_VALUES.views}`);

  const classes = classNames('buy-acess-table', className);

  const {
    refetchFlag,
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const TableHeading = useMemo(
    () => (
      <thead>
        <tr>
          {TABLE_COLUMNS.map((col) => {
            const content = t(COPY_HEADING_CELL[col]);
            const sortingVal = SORTING_VALUES[col];

            return (
              <th key={col}>
                {sortingVal ? (
                  <SortingButton
                    content={content}
                    sortingValue={sortingVal}
                    popupContent={t(POPUP_SORT_NAME[sortingVal])}
                    sort={sortApplied}
                    updateSort={setSortApplied}
                  />
                ) : (
                  content
                )}
              </th>
            );
          })}
        </tr>
      </thead>
    ),
    [sortApplied, t],
  );

  const updateMember = useCallback(
    (member: DataRoomMember) =>
      setMembers((old) =>
        old.map((el) => (el._id === member._id ? { ...el, ...member } : el)),
      ),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items },
        } = await dataRoomApi.room.getMembers(dataRoomId, {
          $page: FIRST_PAGE,
          $sort: sortApplied,
        });
        setMembers(items);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dataRoomId, sortApplied, refetchFlag, popServerError]);

  return (
    <div className={classes}>
      <div className="buy-acess-table__heading">
        <p>{t('buySideAcess')}</p>
      </div>
      {!!members?.length && (
        <div className="buy-acess-table__scrolling-container">
          <table className="buy-acess-table__table">
            {TableHeading}
            <tbody>
              {members.map((member) => (
                <tr key={member._id}>
                  {TABLE_COLUMNS.map((col) => {
                    const sortName = SORTING_VALUES[col];
                    const isSelected =
                      sortName && sortApplied?.includes(sortName);
                    const noAccess =
                      !member.accessableDataRoomNodesView?.length;

                    return (
                      <td
                        key={col}
                        className={classNames({
                          selected: isSelected,
                          noAccess,
                        })}
                      >
                        {BODY_CONTENT_GENERATE[col](member, updateMember)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isLoading && <Loader />}
    </div>
  );
};

export default BuyAcessTable;
